import type { SxProps } from '@mui/material';
import {
    Button,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { smallScreenTab } from '@/src/shared/constants/MediaQueryConstants';
import { useInputValue, useSelectValue } from '@/src/shared/hooks';
import type { FeedbackFormCardProps } from '@/src/shared/types';
import { placeholderGrayColor } from '@/src/styles/theme/lightThemeOptions';

import { AssistantLikeCardLayout } from './AssistantLikeCardLayout';

const ShardSx: SxProps = {
    width: '420px',
    fontFamily: 'Equip_Regular',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
};
const ReasonSelectSx: SxProps = {
    height: '32px',
    ...ShardSx,
    '@media (max-width: 767px)': {
        width: '244px',
        height: '48px',
        '& .MuiSelect-select': {
            whiteSpace: 'normal !important',
        },
    },
};
const CommentInputSx: SxProps = {
    height: '62px',
    ...ShardSx,
    '@media (max-width: 767px)': {
        width: '244px',
        height: '78px',
    },
};
const ButtonSx = (disabled: boolean): SxProps => ({
    textTransform: 'none',
    ...(disabled && {
        pointerEvents: 'none',
    }),
});

export const FeedbackFormCard = ({
    timestamp,
    feedback,
    onSubmit,
    onCancel,
}: FeedbackFormCardProps) => {
    const { t } = useTranslation('ginger');
    const disabled = useMemo(() => !!feedback, [feedback]);
    const [reason, onReasonChange] = useSelectValue(() => feedback?.reason || '');
    const [comment, onCommentChange] = useInputValue(() => feedback?.comment || '');
    const isMobileScreen: boolean = useMediaQuery(smallScreenTab);
    const reasonOptions = useMemo(
        () =>
            Array(6)
                .fill('')
                .map((_, i) => t(`ginger.feedback.form.reason${i + 1}`)),
        [t]
    );
    const onSubmitClick = useCallback(() => {
        onSubmit?.(reason, comment);
    }, [reason, comment]);

    return (
        <AssistantLikeCardLayout timestamp={timestamp}>
            <Stack gap="8px">
                <Typography sx={{ fontSize: '14px' }}>{t('ginger.feedback.form.title')}</Typography>
                <Stack gap="16px" margin={isMobileScreen ? '8px' : '16px'}>
                    <Stack gap="4px">
                        <Select
                            sx={ReasonSelectSx}
                            value={reason}
                            onChange={onReasonChange}
                            disabled={disabled}
                            displayEmpty
                            renderValue={(selected) =>
                                selected || (
                                    <Typography
                                        style={{
                                            color: placeholderGrayColor,
                                            fontSize: '14px',
                                        }}
                                    >
                                        {t('ginger.feedback.form.dropdown.placeholder')}
                                    </Typography>
                                )
                            }
                        >
                            {reasonOptions.map((r) => (
                                <MenuItem key={r} value={r}>
                                    {r}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                    <Stack gap="4px">
                        <OutlinedInput
                            sx={CommentInputSx}
                            multiline
                            maxRows={2}
                            value={comment}
                            onChange={onCommentChange}
                            disabled={disabled}
                            placeholder={t('ginger.feedback.form.text.placeholder') || ''}
                        />
                    </Stack>
                    <Stack direction="row-reverse" gap="10px">
                        <Button variant="contained" sx={ButtonSx(disabled)} onClick={onSubmitClick}>
                            {t('action.submit', { ns: 'common' })}
                        </Button>
                        <Button variant="outlined" sx={ButtonSx(disabled)} onClick={onCancel}>
                            {t('modal.button.error.cancel', { ns: 'common' })}
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </AssistantLikeCardLayout>
    );
};
